import { useRef, useState } from 'react';

import { Typography, Collapse, Checkbox, Space, Tag } from 'antd';
import {
  BlockOutlined,
  CodeSandboxOutlined,
  CodepenOutlined,
  RightOutlined,
} from '@ant-design/icons';

import ProductHierarchyTree from './ProductHierarchyTree';

import { HIERARCHY_LEVEL } from 'static/Constants';
import Messages from 'i18n/messages/product';
import { useIntl } from 'react-intl';

import classnames from 'classnames';

const { Title } = Typography;
const { Panel } = Collapse;

/*
  Why do I clone IXOneHierarchyTree?
    In the future, i'll change a lot of thing on this component and refactor some stuff
*/

export const IXOneHierarchyTree = (props) => {
  const {
    selectedHierarchy,
    hierarchyData,
    publishPackageList,
    isInCheckPublishErrorMode,
    onSelectHierarchy,
  } = props;

  const intl = useIntl();

  const [activeKeys, setActiveKeys] = useState(() =>
    selectedHierarchy?.productHierarchyId
      ? [selectedHierarchy.productHierarchyId]
      : []
  );

  const refAtleastOneLevelActive = useRef([]);

  const getTitle = (item) => {
    if (!item) return;

    const typeText = intl.formatMessage(Messages[`hierarchyType${item.type}`]);

    const gtin = item.gtinHighestLevel ? item.gtinHighestLevel : '';

    const feeText =
      item.fee !== null && item.fee !== undefined ? ` ($${item.fee})` : '';

    return `${typeText} ${gtin ? gtin + ' ' : ''}${
      item.packDescription ? `(${item.packDescription})` : ''
    }${feeText}`;
  };

  const toggleExpanded = (id) => {
    setActiveKeys((prev) =>
      prev.includes(id) ? prev.filter((k) => k !== id) : [...prev, id]
    );
  };

  const onClickPanelHeader = (item) => () => {
    if (!item) return;

    const { id } = item;

    if (selectedHierarchy?.productHierarchyId === id) {
      onSelectHierarchy({});
    } else {
      const hierarchyItemData = hierarchyData.find(
        (hierarchy) => hierarchy.productHierarchyId === id
      );
      onSelectHierarchy(hierarchyItemData);
    }

    toggleExpanded(id);
  };

  const onClickExpandIcon = (panelKey) => () => {
    const item = formattedHierarchyData.find(
      (hierarchy) => hierarchy.id === +panelKey
    );
    onClickPanelHeader(item)(); // ← was setActiveKeys directly
  };

  const getChecked = (item) => {
    if (!item) return;

    return item.id === selectedHierarchy?.productHierarchyId;
  };

  const formatHierarchyData = () => {
    if (!hierarchyData?.length) return [];

    const formattedData = hierarchyData.map((hierarchy, idx) => {
      const {
        productHierarchyId,
        gtin,
        packDescription,
        productItemLinks,
        status,
        ...otherValues
      } = hierarchy;

      const hierarchyTree = getHierarchyTree({
        productItemLinks,
        levelIndex: 0,
        parentProductItemId: null,
        idx,
        ...otherValues,
      });

      return {
        id: productHierarchyId,
        gtinHighestLevel: gtin,
        type: 'Gtin',
        packDescription,
        hierarchyTree,
        status,
        ...otherValues,
      };
    });

    return formattedData;
  };

  const getHierarchyTree = ({
    productItemLinks,
    levelIndex,
    parentProductItemId,
    isActive,
    idx,
    ...otherValues
  }) => {
    if (!productItemLinks.length) return null;
    if (levelIndex <= 4) {
      const icons = {
        [HIERARCHY_LEVEL.PALLET.NAME]: <CodeSandboxOutlined />,
        [HIERARCHY_LEVEL.MASTERCASE.NAME]: <CodeSandboxOutlined />,
        [HIERARCHY_LEVEL.CASE.NAME]: <CodeSandboxOutlined />,
        [HIERARCHY_LEVEL.INNERPACK.NAME]: <CodepenOutlined />,
        [HIERARCHY_LEVEL.UNIT.NAME]: <BlockOutlined />,
      };

      const hierarchyLevel = Object.values(HIERARCHY_LEVEL);

      const matchedLevelProduct = productItemLinks.filter((product) => {
        return product.parentProductItemId === parentProductItemId;
      });

      if (matchedLevelProduct.length) {
        return matchedLevelProduct.map((product) => {
          const isFoundActive = isInCheckPublishErrorMode
            ? !!publishPackageList.find(
                (packageItem) => packageItem?.ProductGTIN === product?.gtin
              )
            : true;

          if (isFoundActive) {
            let nextRefAtleastOneLevelActive = [
              ...refAtleastOneLevelActive.current,
            ];
            nextRefAtleastOneLevelActive[idx] = true;
            refAtleastOneLevelActive.current = nextRefAtleastOneLevelActive;
          }

          return {
            label: product.packageLevel,
            upc12: product.upc12,
            gtin: product.gtin,
            icon: icons[hierarchyLevel[levelIndex].NAME],
            nest: getHierarchyTree({
              productItemLinks,
              levelIndex: levelIndex + 1,
              parentProductItemId: product.productItemId,
              isActive: isFoundActive || isActive,
              idx,
              ...otherValues,
            }),
            productId: product.productItemId,
            count: product.quantity,
            isActive: isFoundActive || isActive,
          };
        });
      }

      return getHierarchyTree({
        productItemLinks,
        levelIndex: levelIndex + 1,
        parentProductItemId,
        idx,
        isActive,
        ...otherValues,
      });
    }
  };

  const formattedHierarchyData = formatHierarchyData();

  const renderExpandIcon = (props) => {
    return (
      <RightOutlined
        style={{ padding: 5 }}
        rotate={props.isActive ? 90 : 0}
        onClick={onClickExpandIcon(props.panelKey)}
      />
    );
  };

  return (
    <Collapse
      className='product-hierarchy__list'
      defaultActiveKey={formattedHierarchyData[0]?.id}
      activeKey={activeKeys}
      expandIcon={renderExpandIcon}
      ghost
    >
      {formattedHierarchyData.map((item, panelIdx) => {
        const isDeleted = item.status === 'Deleted';
        const isHidden =
          isInCheckPublishErrorMode &&
          refAtleastOneLevelActive.current?.[panelIdx] !== true;

        return (
          <Panel
            className={classnames('product-hierarchy__item', {
              'product-hierarchy__item--hidden': isHidden,
            })}
            header={
              <Title
                className='product-hierarchy__item-title'
                level={4}
                ellipsis
                onClick={onClickPanelHeader(item)}
              >
                {getTitle(item)}
                <Checkbox
                  style={{ marginLeft: 8 }}
                  checked={getChecked(item)}
                  disabled={isDeleted}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => {
                    if (selectedHierarchy?.productHierarchyId === item.id) {
                      onSelectHierarchy({});
                    } else {
                      const hierarchyItemData = hierarchyData.find(
                        (h) => h.productHierarchyId === item.id
                      );
                      onSelectHierarchy(hierarchyItemData);
                    }
                  }}
                />
              </Title>
            }
            key={item.id}
            extra={
              <Space>
                {isDeleted ? (
                  <Tag color='error'>
                    {intl.formatMessage(Messages.deletedHierarchy)}
                  </Tag>
                ) : null}
              </Space>
            }
            forceRender
          >
            {item.hierarchyTree?.length
              ? item.hierarchyTree.map((tree, index) => {
                  return (
                    <ProductHierarchyTree
                      key={index}
                      id={item?.id}
                      {...tree}
                      isContainer
                    />
                  );
                })
              : null}
          </Panel>
        );
      })}
    </Collapse>
  );
};
